import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-alpine.css';

interface TeamStatsTableProps {
  homeTeamName: string;
  oppTeamName: string;
  homeStats: Record<string, number>;
  oppStats: Record<string, number>;
  homeTeamCode: number;
  oppTeamCode: number;
}

const TeamStatsTable: React.FC<TeamStatsTableProps> = ({
  homeTeamName,
  oppTeamName,
  homeStats,
  oppStats,
  homeTeamCode,
  oppTeamCode
}) => {
  
  // Add formatter function for decimal values
  const formatStatValue = (params: any) => {
    const value = params.value;
    if (value === null || value === undefined) return "-";
    
    const numValue = typeof value === "number" ? value : parseFloat(String(value));
    
    if (isNaN(numValue)) return String(value);
    
    // Check if it's a decimal number
    if (!Number.isInteger(numValue)) {
      return numValue.toFixed(2);
    }
    
    return String(numValue);
  };

  const rowData = useMemo(() => {
    const keys = Array.from(new Set([...Object.keys(homeStats), ...Object.keys(oppStats)]));
    return keys.map((key) => ({
      Stat: key.replace(/_/g, ' ').toUpperCase(),
      [homeTeamName]: homeStats[key] ?? 0,
      [oppTeamName]: oppStats[key] ?? 0,
    }));
  }, [homeStats, oppStats, homeTeamName, oppTeamName]);

  const columnDefs = useMemo(() => [
    { 
      headerName: "Stat", 
      field: "Stat", 
      sortable: true, 
      filter: true, 
      flex: 2,
      minWidth: 200,
      cellStyle: { whiteSpace: 'nowrap', textAlign: 'left' } as any,
      headerClass: 'stat-header'
    },
    { 
      headerName: homeTeamName, 
      field: homeTeamName, 
      sortable: true, 
      filter: true, 
      flex: 1.5,
      minWidth: 120,
      cellStyle: { textAlign: 'center' } as any,
      headerClass: 'team-header',
      valueFormatter: formatStatValue
    },
    { 
      headerName: oppTeamName, 
      field: oppTeamName, 
      sortable: true, 
      filter: true, 
      flex: 1.5,
      minWidth: 120,
      cellStyle: { textAlign: 'center' } as any,
      headerClass: 'team-header',
      valueFormatter: formatStatValue
    },
  ] as any[], [homeTeamName, oppTeamName]);

  const homeLogo = `https://premier.athlytesports.com/assets/images/teams/${homeTeamCode}.png`;
  const oppLogo = `https://premier.athlytesports.com/assets/images/teams/${oppTeamCode}.png`;

  return (
    <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto mb-6">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <img 
            src={homeLogo} 
            alt={homeTeamName} 
            className="w-6 h-6 mr-1" 
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }} 
          />
          <span className="font-semibold">{homeTeamName}</span>
        </div>
        <div className="flex items-center">
          <span className="font-semibold">{oppTeamName}</span>
          <img 
            src={oppLogo} 
            alt={oppTeamName} 
            className="w-6 h-6 ml-1" 
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      </div>
      <div className="ag-theme-alpine" style={{ 
        width: '100%',
        minWidth: '100%',
        overflow: 'hidden'
      }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            suppressMovable: true,
            suppressSizeToFit: false,
            autoHeaderHeight: true
          }}
          suppressColumnVirtualisation={true}
          domLayout='autoHeight'
          headerHeight={40}
          rowHeight={35}
          suppressHorizontalScroll={true}
          suppressFieldDotNotation={true}
          onGridReady={(event: GridReadyEvent) => {
            const { api } = event;
            // Ensure columns take full width
            api.sizeColumnsToFit();
          }}
          onGridSizeChanged={(event) => {
            // Adjust columns when container size changes
            event.api.sizeColumnsToFit();
          }}
        />
      </div>
    </div>
  );
};

export default TeamStatsTable;
